import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, catchError, of, switchMap, take } from 'rxjs';
import { PodcastService } from '../../services/podcast.service';
import * as podcastActions from '../actions/podcast.actions';
import * as userActions from '../actions/user.actions';

@Injectable()
export class PodcastEffects {
  actions$ = inject(Actions);
  podcastService: PodcastService = inject(PodcastService);
  toastr: ToastrService = inject(ToastrService);
  router: Router = inject(Router);

  private logout$ = new Subject<void>();

  constructor() {}

  // effect to fetch use podcasts from AWS using http call
  getPodcasts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(podcastActions.getPodcasts),
      switchMap(async ({ uid }) => {
        this.logout$.next();
        return await this.podcastService
          .getPodcasts(uid)
          .then((response) => response);
      }),
      switchMap((response: Observable<any>) =>
        response.pipe(
          take(1),
          switchMap((response) =>
            of(
              podcastActions.loadPodcast({
                podcasts: response['result'],
                lastSearchKey: response['lastSearchKey'],
              })
            )
          )
        )
      ),
      catchError((error) => {
        // console.error(`Error fetching podcasts: ${error}`);
        return of(podcastActions.getPodcastsFailure({ error }));
      })
    )
  );

  // effect to create a podcast
  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(podcastActions.create),
      switchMap(
        async ({ podcast }) => await this.podcastService.create(podcast)
      ),
      switchMap((podcast) => {
        this.toastr.success(
          "The Podcast is created in 'Draft' mode. You will be notified once it is published.",
          'Podcast Created Successfully'
        );
        this.router.navigate(['/podcasts']);
        return of(podcastActions.createSuccess({ podcast }));
      }),
      catchError((error) => {
        // console.error(`Error creating podcast: ${error}`);
        this.toastr.error(
          `An error occurred, '${error}'. Please try again later.`,
          'Error Creating Podcast'
        );
        return of(podcastActions.createFailure({ error }));
      })
    )
  );

  // effect to update a podcast
  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(podcastActions.update),
      switchMap(
        async ({ podcast }) => await this.podcastService.update(podcast)
      ),
      switchMap((podcast) => {
        this.toastr.success(
          'Podcast details updated successfully. The information might take a while to refresh.',
          'Podcast Details Updated'
        );
        this.router.navigate(['/podcasts']);
        return of(podcastActions.updateSuccess({ podcast }));
      }),
      catchError((error) => {
        // console.error(`Error updating podcast: ${error}`);
        this.toastr.error(
          `An error occurred, '${error}'. Please try again later.`,
          'Error Updating Podcast'
        );
        return of(podcastActions.updateFailure({ error }));
      })
    )
  );

  // effect to delete a podcast
  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(podcastActions.deleteAction),
      switchMap(async ({ id }) => await this.podcastService.delete(id)),
      switchMap((resultId) => {
        this.toastr.success(
          'The podcast has been deleted successfully.',
          'Podcast Deleted'
        );
        this.router.navigate(['/podcasts']);
        return of(podcastActions.deleteSuccess({ id: resultId }));
      }),
      catchError((error) => {
        // console.error(`Error deleting podcast: ${error}`);
        this.toastr.error(
          `An error occurred, '${error}'. Please try again later.`,
          'Error Deleting Podcast'
        );
        return of(podcastActions.deleteFailure({ error }));
      })
    )
  );

  clearPodcasts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logoutSuccess),
      switchMap(() => {
        this.logout$.next();
        return of(podcastActions.clear());
      })
    )
  );
}
